import cls from 'classnames'
import { min, reduce } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { sizeFontWeighMap, sizeMap } from './constant'
import type { Size } from './types'
import { measureFontLineHeight } from './utils'

interface BaseInputProps {
    value?: string | number
    leftSection?: React.ReactNode
    rightSection?: React.ReactNode
    pattern?: string
    isWrap?: boolean
    className?: string
    style?: React.CSSProperties
    readOnly?: boolean
    size?: Size
    fontSize?: string
    minHeight?: number
    fontFamily?: string
    fontWeight?: number
    placeholderColor?: string
    hideText?: boolean
    inputProps?: React.InputHTMLAttributes<HTMLTextAreaElement>
}

const SCxContainer = styled.div`
    position: relative;
    width: 100%;
    min-height: 38px;
    display: flex;
    padding: 9px 7px;
    cursor: text;

    // 增加选择器权重防止被简单覆盖
    &.readonly.readonly.readonly {
        /* background-color: var(--color-gray-50); */
        cursor: not-allowed;
    }
`

const SCxInputWrapper = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    flex: 1;
    display: flex;
    align-items: center;
`

const SCxInput = styled.textarea<{
    textSize: Size
    customFontSize?: string
    customFontWeight?: number
    isWrap?: boolean
    placeholderColor?: string
}>`
    width: 100%;
    height: ${({ isWrap }) => (isWrap !== false ? '100%' : 'auto')};
    padding: 0;
    outline: none;
    font-size: ${({ textSize, customFontSize }) => customFontSize || `${sizeMap[textSize]}px`};
    font-weight: ${({ textSize, customFontWeight }) => customFontWeight ?? sizeFontWeighMap[textSize]};
    line-height: ${({ customFontSize }) => (customFontSize ? 'normal' : '20px')};
    resize: none;
    overflow: hidden;
    word-break: break-word;
    min-height: 20px;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    color: inherit;
    cursor: inherit;

    /* appearance: none; */
    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    /* Firefox */
    appearance: textfield;

    ::placeholder {
        font-weight: ${({ textSize, customFontWeight }) => customFontWeight ?? sizeFontWeighMap[textSize]};
        color: ${({ placeholderColor }) => placeholderColor || 'var(--color-gray-300)'};
    }
`

const SCxPreview = styled.p<{ textSize: Size; isWrap?: boolean; customFontSize?: string; customFontWeight?: number }>`
    position: relative;
    height: auto;
    max-width: 100%;
    line-height: ${({ customFontSize }) => (customFontSize ? 'normal' : '20px')};
    min-height: 20px;
    flex: 1;
    font-size: ${({ textSize, customFontSize }) => customFontSize || `${sizeMap[textSize]}px`};
    font-weight: ${({ textSize, customFontWeight }) => customFontWeight ?? sizeFontWeighMap[textSize]};
    word-break: break-word;
    white-space: ${({ isWrap }) => (isWrap ? 'pre-wrap' : 'nowrap')};
    font-family: inherit;
    overflow: hidden;
`

const BaseInput = React.forwardRef<HTMLTextAreaElement, BaseInputProps>(
    (
        {
            rightSection,
            size = 'middle',
            readOnly,
            leftSection,
            placeholderColor,
            minHeight,
            fontSize,
            fontFamily,
            fontWeight,
            value,
            className,
            isWrap = true,
            inputProps,
            hideText,
            style,
            ...rest
        },
        ref
    ) => {
        const innerRef = useRef<HTMLTextAreaElement>(null)

        const handleWrapperClick = useCallback(() => {
            innerRef.current?.focus()
        }, [])

        const height = useMemo(() => {
            if (!isWrap || typeof value !== 'string') {
                return
            }
            const [enterNum] = reduce<string, [number, boolean]>(
                ([num, isEnd], curVal) => {
                    if (curVal === '\n' && isEnd) {
                        return [num + 1, true]
                    }
                    return [num, false]
                },
                [0, true],
                value?.split('').reverse() ?? []
            )
            const computeHeight = min(enterNum * 20, 20)
            return value?.endsWith('\n') ? computeHeight : undefined
        }, [isWrap, value])

        // 用 state 存储测量结果，使得字体加载完成后可以触发重新测量
        const [fontLineHeight, setFontLineHeight] = useState<number | undefined>(() => {
            if (!fontSize) return undefined
            const h = measureFontLineHeight(fontSize, fontFamily)
            return h != null ? h + 18 : undefined
        })

        useEffect(() => {
            if (!fontSize) {
                setFontLineHeight(undefined)
                return
            }
            const measure = () => {
                const h = measureFontLineHeight(fontSize, fontFamily)
                if (h != null) setFontLineHeight(h + 18)
            }
            // 立即重测一次（修正 lazy init 与 effect 之间可能的字体状态差异）
            measure()
            // 每当有字体加载完成时重测（含 StyleContent 异步加载的自定义字体）
            document.fonts.addEventListener('loadingdone', measure)
            return () => {
                document.fonts.removeEventListener('loadingdone', measure)
            }
        }, [fontSize, fontFamily])

        const containerStyle = useMemo<React.CSSProperties>(() => {
            if (!fontSize || fontLineHeight == null)
                return {
                    ...style,
                    minHeight: minHeight ?? 0
                }
            const fontBasedMinHeight = fontLineHeight
            return {
                ...style,
                minHeight: Math.max(minHeight ?? 0, fontBasedMinHeight)
            }
        }, [fontSize, fontLineHeight, minHeight, style])

        return (
            <SCxContainer tabIndex={0} className={cls([{ readonly: readOnly }, className])} style={containerStyle} {...rest}>
                <SCxPreview
                    textSize={size}
                    isWrap={isWrap}
                    customFontSize={fontSize}
                    customFontWeight={fontWeight}
                    style={{ paddingBottom: height }}
                >
                    {!hideText && <span style={{ color: 'transparent', userSelect: 'none', pointerEvents: 'none' }}>{value}</span>}
                    <SCxInputWrapper>
                        {leftSection}
                        <SCxInput
                            textSize={size}
                            customFontSize={fontSize}
                            customFontWeight={fontWeight}
                            placeholderColor={placeholderColor}
                            {...inputProps}
                            value={value}
                            readOnly={readOnly}
                            ref={ref}
                        />
                    </SCxInputWrapper>
                </SCxPreview>
                {rightSection}
            </SCxContainer>
        )
    }
)

export default BaseInput
